/** DocuSign 連携。秘密入力・認可結果は query cache に保存しない。 */
import { useCallback } from 'react';
import { apiFetch } from '@/api/client';
import { useFetch, useMutation } from '@/hooks/useApi';
import type {
  DocuSignStatus,
  DocuSignConfigInput,
  DocuSignAccountSelection,
  DocuSignTemplate,
} from '@/types/api-types';

export function useDocuSignStatus(enabled = true) {
  return useFetch<DocuSignStatus>(enabled ? '/admin/docusign/status' : null);
}
export function useSetDocuSignConfig() {
  const fn = useCallback(
    (input: DocuSignConfigInput) =>
      apiFetch<DocuSignStatus>('/admin/docusign/config', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    [],
  );
  return useMutation<DocuSignConfigInput, DocuSignStatus>(fn);
}
export function useDisconnectDocuSign() {
  const fn = useCallback(
    () => apiFetch<DocuSignStatus>('/admin/docusign/config', { method: 'DELETE' }),
    [],
  );
  return useMutation<void, DocuSignStatus>(fn);
}
export function useAuthorizeDocuSign() {
  const fn = useCallback(
    () => apiFetch<{ authorizationUrl: string }>('/admin/docusign/authorize', { method: 'POST' }),
    [],
  );
  return useMutation<void, { authorizationUrl: string }>(fn);
}
/** 認可コードは一度だけ送信し、自動再試行しない。 */
export function completeDocuSignAuthorization(input: { code: string; state: string }) {
  return apiFetch<DocuSignAccountSelection>('/admin/docusign/oauth/callback', {
    method: 'POST',
    redirectOnSessionExpired: false,
    body: JSON.stringify(input),
  });
}
export function useSelectDocuSignAccount() {
  const fn = useCallback(
    (accountId: string) =>
      apiFetch<DocuSignStatus>('/admin/docusign/account', {
        method: 'PUT',
        body: JSON.stringify({ accountId }),
      }),
    [],
  );
  return useMutation<string, DocuSignStatus>(fn);
}
export function useDocuSignTemplates(enabled: boolean) {
  return useFetch<{ templates: DocuSignTemplate[] }>(enabled ? '/admin/docusign/templates' : null);
}
export function useDocuSignTemplate(templateId: string | null) {
  return useFetch<{ template: DocuSignTemplate }>(
    templateId ? `/admin/docusign/templates/${encodeURIComponent(templateId)}` : null,
  );
}
